/**
 * "Jazz Piano First Steps" trial CTA — audience rules.
 *
 * Hub CTA rows with target `trial_beginner_piano` (copy lives in the CMS) are
 * shown, persistently and without dismissal, to members who are on their free
 * trial and answered the survey as beginner (any instrument) or intermediate
 * piano — the same rule the WP thank-you page uses to pin the course.
 *
 * "On trial" comes from `user_subscription_state.trial_end_at` (WooCommerce
 * Subscriptions trial_end, synced by the WP bridge). Fallback for rows that
 * predate that field: the WP bridge creates the Supabase auth user at WP
 * registration and a 14-day trial's first charge lands 14 days after that, so
 * a member whose first charge (`next_payment_at`, or `access_ends_at` once
 * they pending-cancel) is still ahead AND within TRIAL_MAX_DAYS of
 * `auth.users.created_at` is treated as on trial. That fallback misses old
 * accounts (e.g. imported in 2025) that start a trial now.
 */
import {
  normalizeInstrumentSlug,
  normalizeLevelKey,
  parseHomeSurveyConfig,
} from "@/lib/dashboardHomeSurvey";
import type { CourseMetadata } from "@/types";
import type { HubCtaTarget } from "@/types/hubCtas";
import type { UserSubscriptionStateRow } from "@/types/userSubscriptionState";

export const TRIAL_CTA_TARGET: HubCtaTarget = "trial_beginner_piano";

/** WP free trial is 14 days; allow slack for timezone / same-day sync offsets. */
export const TRIAL_MAX_DAYS = 16;

const DAY_MS = 86_400_000;

/**
 * Trial members who skipped the survey have not told us they are *not*
 * beginners; keep showing them the course. Flip to false to require answers.
 */
export const TRIAL_CTA_INCLUDE_NO_SURVEY = true;

const TRIAL_SUBSCRIPTION_STATUSES = new Set(["active", "pending-cancel"]);

type PreferenceMetadata = Record<string, unknown> | null | undefined;

/**
 * On trial = `trial_end_at` (synced from WooCommerce Subscriptions) is still
 * ahead. Rows synced before the bridge sent that field have it null; for those
 * fall back to "first charge still ahead and within TRIAL_MAX_DAYS of the
 * Supabase join date" (see file header — this misses old accounts that start
 * a trial later, which is exactly why trial_end_at exists).
 */
export function isOnTrial(
  row: Pick<
    UserSubscriptionStateRow,
    "next_payment_at" | "access_ends_at" | "trial_end_at"
  >,
  createdAt: string | null | undefined,
  now: number = Date.now(),
): boolean {
  if (row.trial_end_at) {
    const trialEnd = Date.parse(row.trial_end_at);
    if (Number.isFinite(trialEnd)) return trialEnd > now;
  }

  if (!createdAt) return false;
  const created = Date.parse(createdAt);
  if (!Number.isFinite(created) || created > now) return false;

  const firstChargeRaw = row.next_payment_at ?? row.access_ends_at;
  if (!firstChargeRaw) return false;
  const firstCharge = Date.parse(firstChargeRaw);
  if (!Number.isFinite(firstCharge)) return false;

  if (firstCharge <= now) return false;
  return firstCharge - created <= TRIAL_MAX_DAYS * DAY_MS;
}

/** Beginner (any instrument) or intermediate + piano; optionally "no answers yet". */
export function surveyMatchesTrialCta(metadata: PreferenceMetadata): boolean {
  const level = normalizeLevelKey(metadata?.level);
  const instrument = normalizeInstrumentSlug(metadata?.instrument);

  if (!level && !instrument) return TRIAL_CTA_INCLUDE_NO_SURVEY;
  if (level === "beginner") return true;
  return level === "intermediate" && instrument === "piano";
}

/** Web / WooCommerce path: membership subscription row says the trial is running. */
export function isSubscriptionRowOnTrial(
  row: UserSubscriptionStateRow | null,
  userCreatedAt: string | null | undefined,
): boolean {
  if (!row) return false;
  if (row.is_lifetime === true) return false;
  if (row.tier !== "regular" && row.tier !== "pro") return false;
  if (!TRIAL_SUBSCRIPTION_STATUSES.has(row.subscription_status ?? "")) {
    return false;
  }
  return isOnTrial(row, userCreatedAt);
}

export function isTrialCourseCtaEligible(input: {
  subscription: UserSubscriptionStateRow | null;
  userCreatedAt: string | null | undefined;
  preferenceMetadata: PreferenceMetadata;
  /**
   * Apple (RevenueCat) trial, iOS only — Apple signups have no subscription
   * row, so this is their only trial signal. See lib/purchases/useAppleTrial.ts.
   */
  appleTrial?: { onTrial: boolean } | null;
}): boolean {
  const onTrial =
    isSubscriptionRowOnTrial(input.subscription, input.userCreatedAt) ||
    input.appleTrial?.onTrial === true;
  if (!onTrial) return false;
  return surveyMatchesTrialCta(input.preferenceMetadata);
}

/** Dashboards that carry the trial CTA: piano home dashboards for level 1 / 2 (JPF, JPE). */
export function dashboardShowsTrialCta(
  courseMetadata: CourseMetadata | null | undefined,
): boolean {
  const cfg = parseHomeSurveyConfig(courseMetadata);
  if (!cfg) return false;
  if (normalizeInstrumentSlug(cfg.instrument) !== "piano") return false;
  const level = normalizeLevelKey(cfg.level);
  return level === "beginner" || level === "intermediate";
}
